import * as React from "react";
import { isEqual } from "lodash";
import classnames from "classnames";

import "./svg.draggable.js";
import { BoardSvg, Piece } from "@private/chess2-board";

// Are two Coordinates the same value?
const coordinatesEqual = (x: Coordinate, y: Coordinate) =>
  x[0] === y[0] && x[1] === y[1];
type Coordinate = [number, number];
type ParsedFen = ReadonlyArray<ReadonlyArray<string | null>>;

type Props = {
  stones: [number, number];
  pieces: ParsedFen;
  // Additional classNames to apply to each square. Note the
  // `Board.squareClasses` property.
  squareClassName?: (coordinate: Coordinate) => string | null;
  selectableSquares?: Array<Coordinate>;
  listReachableFrom?: (coordinate: Coordinate) => Array<Coordinate>;
  // Should moves be sent when the destination square is unreachable?
  moveToUnreachable?: boolean;
  onMove?: (from: Coordinate, to: Coordinate) => void;
  onSelectedSquareChange?: (arg0: Coordinate | undefined) => void;
  flipped?: boolean;
  children?: React.ReactNode;
};

type State = {
  hoverSquare: Coordinate | null;
  selectedSquare?: Coordinate | undefined;
};

export default class Board extends React.Component<
  Props & Omit<React.HTMLProps<HTMLDivElement>, "ref">,
  State
> {
  static squareClasses = BoardSvg.squareClasses;

  state = { hoverSquare: null, selectedSquare: undefined };

  root?: HTMLDivElement;
  svg!: BoardSvg;
  isDragging?: boolean;
  draggedPiece?: Piece;

  setPieces(pieces: ParsedFen) {
    this.svg.setPieces(pieces);

    // Clear out any pending actions.
    this.updateSelectedSquare(undefined);
    this.isDragging = false;
    this.draggedPiece = undefined;
  }

  getSelectedSquare(): Coordinate | undefined {
    return this.state.selectedSquare;
  }

  handleSquareHover(square: Coordinate | null, event: Event) {
    this.setState({ hoverSquare: square });
  }

  handleSquareClick(coords: Coordinate, event: Event) {
    const selectedSquare = this.state.selectedSquare;
    const targetSquare = this.svg.squares[coords[1]][coords[0]];
    if (selectedSquare) {
      if (isEqual(selectedSquare, coords)) {
        this.updateSelectedSquare(undefined);
        return;
      } else if (this._canMoveTo(coords)) {
        this.updateSelectedSquare(undefined);
        if (this.props.onMove) {
          this.props.onMove(selectedSquare, coords);
        }
        return;
      }
    }
    if (targetSquare.hasClass(Board.squareClasses.selectable)) {
      this.updateSelectedSquare(coords);
    }
  }

  handleBeforeDrag = (e: CustomEvent) => {
    const box = (e.target as any).instance.bbox();
    // These end up being the middle of the square clicked.
    const x = Math.floor(box.x / BoardSvg.squareSize);
    const y = Math.floor(box.y / BoardSvg.squareSize);
    const targetSquare = this.svg.squares[y][x];
    if (
      !targetSquare ||
      !targetSquare.hasClass(Board.squareClasses.selectable)
    ) {
      e.preventDefault();
    }
  };

  handleDragMove = (e: CustomEvent) => {
    const { sx, sy, dx, dy, x, y } = this._getDragCoords(e);

    const dragStartCoord: Coordinate = [
      Math.floor(sx / BoardSvg.squareSize),
      Math.floor(sy / BoardSvg.squareSize),
    ];
    const dragStartSquare = this.svg.squares[dragStartCoord[1]][
      dragStartCoord[0]
    ];
    if (
      dragStartSquare &&
      dragStartSquare.hasClass(Board.squareClasses.selectable) &&
      (Math.abs(dx) >= 3 || Math.abs(dy) >= 3) &&
      !this.isDragging
    ) {
      this.isDragging = true;
      this.updateSelectedSquare(dragStartCoord);
      const draggedPiece = this.svg.pieces.find((p: any) =>
        coordinatesEqual(p.coord, dragStartCoord)
      );
      if (draggedPiece) {
        this.draggedPiece = draggedPiece;
        draggedPiece.node.front();
        draggedPiece.ghost = draggedPiece.clone();
        draggedPiece.ghost.node.after(draggedPiece.node).opacity(0.5);
      }
    }

    e.preventDefault();
    if (this.draggedPiece) {
      this.draggedPiece.move(x, y);
    }
  };

  handleDragEnd = (e: CustomEvent) => {
    const draggedPiece = this.draggedPiece;
    const ghost = draggedPiece && draggedPiece.ghost;
    if (draggedPiece) {
      if (ghost) {
        // Put the dragged piece back on top of the ghost and delete the ghost.
        const matrix = ghost.node.transform();
        draggedPiece.node.transform(matrix);
        ghost.remove();
        draggedPiece.ghost = undefined;
      }
      this.draggedPiece = undefined;
    }
    const selectedSquare = this.state.selectedSquare;
    if (this.isDragging && selectedSquare) {
      const { sqx, sqy } = this._getDragCoords(e);
      const dropSquare: Coordinate = [sqx, sqy];
      const validDrop = sqx >= 0 && sqy >= 0 && sqx < 8 && sqy < 8;
      const moved = !coordinatesEqual(selectedSquare, dropSquare);
      if (validDrop && moved && this._canMoveTo(dropSquare)) {
        if (this.props.onMove) {
          this.props.onMove(selectedSquare, dropSquare);
        }
      }
      this.updateSelectedSquare(undefined);
      this.isDragging = false;
    }
  };

  _getDragCoords(e: CustomEvent) {
    const p = e.detail.p;
    const handler = e.detail.handler;
    // This math comes from the base library's default drag function.
    const [sx, sy] = [handler.startPoints.box.x, handler.startPoints.box.y];
    const [dx, dy] = [
      p.x - handler.startPoints.point.x,
      p.y - handler.startPoints.point.y,
    ];
    const [x, y] = [sx + dx, sy + dy];
    const [sqx, sqy] = [
      Math.floor(p.x / BoardSvg.squareSize),
      Math.floor(p.y / BoardSvg.squareSize),
    ];
    return { sx, sy, dx, dy, x, y, sqx, sqy };
  }

  _canMoveTo(coords: Coordinate) {
    const targetSquare = this.svg.squares[coords[1]][coords[0]];
    return (
      this.props.moveToUnreachable ||
      (targetSquare && targetSquare.hasClass(Board.squareClasses.reachable))
    );
  }

  componentDidMount() {
    const root = this.root;
    if (!root) return;
    this.svg = new BoardSvg(root);
    this.svg.squares.forEach((row, y) =>
      row.forEach((square, x) =>
        square
          .draggable()
          .on("beforedrag", this.handleBeforeDrag)
          .on("dragmove", this.handleDragMove)
          .on("dragend", this.handleDragEnd)
          .on("mouseover", (e: MouseEvent) => this.handleSquareHover([x, y], e))
          .on("mouseout", (e: MouseEvent) => this.handleSquareHover(null, e))
          .on("click", (e: MouseEvent) => this.handleSquareClick([x, y], e))
      )
    );

    this.svg.setPieces(this.props.pieces);
    this._syncShapes();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevProps.pieces !== this.props.pieces) {
      this.setPieces(this.props.pieces);
    }
    this._syncShapes();
  }

  updateSelectedSquare(next: Coordinate | undefined) {
    const prev = this.state.selectedSquare;
    const changed =
      prev && next
        ? !coordinatesEqual(prev, next)
        : !prev && !next
        ? false
        : true;
    if (changed) {
      this.setState({ selectedSquare: next });
      if (this.props.onSelectedSquareChange) {
        this.props.onSelectedSquareChange(next);
      }
    }
  }

  render() {
    const {
      stones,
      pieces,
      squareClassName,
      selectableSquares,
      listReachableFrom,
      moveToUnreachable,
      onMove,
      onSelectedSquareChange,
      flipped,
      ...rest
    } = this.props;
    return <div {...rest} ref={(el: any) => (this.root = el)} />;
  }

  _syncShapes() {
    this.svg.setFlipped(!!this.props.flipped);
    this.svg.setStones(this.props.stones);
    this._syncSquareClasses();
  }

  _syncSquareClasses() {
    const selectedSquare = this.state.selectedSquare;
    const isSquareReachable = Array.from({ length: 8 }).map((_) =>
      Array.from({ length: 8 }).map((_) => false)
    );
    if (this.props.listReachableFrom && selectedSquare) {
      this.props
        .listReachableFrom(selectedSquare)
        .forEach(([x, y]) => (isSquareReachable[y][x] = true));
    }

    const selectableSquares = this.props.selectableSquares;
    const isSquareSelectable = Array.from({ length: 8 }).map((_) =>
      Array.from({ length: 8 }).map((_) => false)
    );
    if (selectableSquares) {
      selectableSquares.forEach(([x, y]) => (isSquareSelectable[y][x] = true));
    }

    const additionalClassNames = this.props.squareClassName;

    this.svg.squares.forEach((row, y) =>
      row.forEach((square, x) => {
        const coord = [x, y] as Coordinate;
        const target = classnames(
          Board.squareClasses.square,
          (x + y) % 2 === 0
            ? Board.squareClasses.white
            : Board.squareClasses.black,
          additionalClassNames ? additionalClassNames(coord) : null,
          {
            [Board.squareClasses.reachable]: isSquareReachable[y][x],
            [Board.squareClasses.selectable]: isSquareSelectable[y][x],
            [Board.squareClasses.selected]:
              selectedSquare && coordinatesEqual(selectedSquare!, coord),
          }
        );
        if (square.attr("class") !== target) {
          square.attr("class", target);
        }
      })
    );
  }
}
